// Click/event tracking. Does nothing until VITE_PLAUSIBLE_DOMAIN is set (e.g.
// "preppos.com"), so the site collects nothing and loads nothing third-party by
// default. Set it in the Lovable project environment and republish to turn
// tracking on. Plausible is cookieless, so no consent banner is needed.
import { clarityEvent } from "@/lib/clarity";
import { emailCampaignFromRef, getSignupRef } from "@/lib/signup-ref";

type Props = Record<string, string | number | boolean>;

declare global {
  interface Window {
    plausible?: (event: string, opts?: { props?: Props }) => void;
  }
}

const SCRIPT_ID = "plausible-script";

function domain(): string | undefined {
  const d = import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined;
  return d && d.trim() ? d.trim() : undefined;
}

function ensureScript(d: string) {
  if (document.getElementById(SCRIPT_ID)) return;
  // Queue calls made before the script finishes loading.
  window.plausible =
    window.plausible ||
    function (...args: unknown[]) {
      ((window.plausible as unknown as { q?: unknown[] }).q =
        (window.plausible as unknown as { q?: unknown[] }).q || []).push(args);
    };
  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.defer = true;
  s.setAttribute("data-domain", d);
  s.src = "https://plausible.io/js/script.tagged-events.js";
  document.head.appendChild(s);
}

export function track(event: string, props?: Props): void {
  clarityEvent(event);
  try {
    const d = domain();
    if (!d) return;
    ensureScript(d);
    // Visitors who arrived from one of our emails carry the campaign name.
    const campaign = emailCampaignFromRef(getSignupRef());
    const all = campaign ? { ...props, email_campaign: campaign } : props;
    window.plausible?.(event, all ? { props: all } : undefined);
  } catch {
    // tracking must never break the page
  }
}

/** Map a clicked link to an event, or null if it isn't one we count. */
export function eventForLink(href: string): { event: string; props?: Props } | null {
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return null;
  }
  if (url.hostname === "cal.com") return { event: "setup_call_click" };
  if (url.origin !== window.location.origin) return null;
  if (url.pathname === "/register") {
    const src = url.searchParams.get("src");
    const plan = url.searchParams.get("plan");
    return {
      event: "trial_click",
      props: { src: src ?? "unknown", ...(plan ? { plan } : {}) },
    };
  }
  if (url.pathname === "/get-started") return { event: "sample_dashboard_click" };
  return null;
}

/** One delegated listener for every trial / call / demo link on the site. */
export function installClickTracking(): () => void {
  const onClick = (e: MouseEvent) => {
    const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a) return;
    const hit = eventForLink(a.getAttribute("href") ?? "");
    if (hit) track(hit.event, hit.props);
  };
  document.addEventListener("click", onClick, true);
  return () => document.removeEventListener("click", onClick, true);
}
